import {
  answerDeletePermission,
  answerEditPermission,
  AnswerUserRelationState,
  Permission,
  permissionSet,
  PlexUserRelationState,
  postDeletePermission,
  postEditPermission,
  PostUserRelationState,
  questionDeletePermission,
  questionEditPermission,
  QuestionUserRelationState,
  taskDeletePermission,
  taskEditPermission,
  TaskUserRelationState,
} from '@all-hands-hq/plex-graphql-schema';

import { PublishStatus, QuestionStatus } from '../../graphql/gen/types';

export function canSubscribe(role: PlexUserRelationState): boolean {
  return role === PlexUserRelationState.SameDomainUser;
}

export function canLeave(role: PlexUserRelationState): boolean {
  return role !== PlexUserRelationState.Owner;
}

export function canCreatePost(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CREATE_PLEX_POST);
}

function hasContentPermission<T>({
  plexRelation,
  contentRelation,
  getRequiredPermission,
}: {
  plexRelation: PlexUserRelationState;
  contentRelation: T;
  getRequiredPermission: (t: T) => Permission | null;
}): boolean {
  const requiredPermission = getRequiredPermission(contentRelation);
  return requiredPermission != null && permissionSet(plexRelation).has(requiredPermission);
}

export function canPublishPost({
  plexRelation,
  status,
}: {
  plexRelation: PlexUserRelationState;
  status: PublishStatus;
}): boolean {
  return status === PublishStatus.Internal && permissionSet(plexRelation).has(Permission.CHANGE_POST_PUBLISH_STATUS);
}

export function canUnpublishPost({
  plexRelation,
  status,
}: {
  plexRelation: PlexUserRelationState;
  status: PublishStatus;
}): boolean {
  return status === PublishStatus.Published && permissionSet(plexRelation).has(Permission.CHANGE_POST_PUBLISH_STATUS);
}

export function canEditPost({
  plexRelation,
  postRelation,
}: {
  plexRelation: PlexUserRelationState;
  postRelation: PostUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: postRelation,
    getRequiredPermission: postEditPermission,
  });
}

export function canDeletePost({
  plexRelation,
  postRelation,
}: {
  plexRelation: PlexUserRelationState;
  postRelation: PostUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: postRelation,
    getRequiredPermission: postDeletePermission,
  });
}

export function canCreateFact(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CREATE_PLEX_QUESTION);
}

export function canManage(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.WRITE_PLEX_SETTINGS);
}

export function canPublishFact({
  plexRelation,
  status,
}: {
  plexRelation: PlexUserRelationState;
  status: QuestionStatus;
}): boolean {
  return (
    status === QuestionStatus.Internal && permissionSet(plexRelation).has(Permission.CHANGE_QUESTION_PUBLISH_STATUS)
  );
}

export function canUnpublishFact({
  plexRelation,
  status,
}: {
  plexRelation: PlexUserRelationState;
  status: QuestionStatus;
}): boolean {
  return (
    status === QuestionStatus.Published && permissionSet(plexRelation).has(Permission.CHANGE_QUESTION_PUBLISH_STATUS)
  );
}

export function canCreateAnswer({ plexRelation }: { plexRelation: PlexUserRelationState }): boolean {
  return permissionSet(plexRelation).has(Permission.CREATE_PLEX_ANSWER);
}

export function canEditQuestion({
  plexRelation,
  questionRelation,
}: {
  plexRelation: PlexUserRelationState;
  questionRelation: QuestionUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: questionRelation,
    getRequiredPermission: questionEditPermission,
  });
}

export function canDeleteQuestion({
  plexRelation,
  questionRelation,
}: {
  plexRelation: PlexUserRelationState;
  questionRelation: QuestionUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: questionRelation,
    getRequiredPermission: questionDeletePermission,
  });
}

export function canEditAnswer({
  plexRelation,
  answerRelation,
}: {
  plexRelation: PlexUserRelationState;
  answerRelation: AnswerUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: answerRelation,
    getRequiredPermission: answerEditPermission,
  });
}

export function canChangeAnswerApprovalStatus({ plexRelation }: { plexRelation: PlexUserRelationState }): boolean {
  return permissionSet(plexRelation).has(Permission.APPROVE_PLEX_ANSWER);
}

export function canDeleteAnswer({
  plexRelation,
  answerRelation,
}: {
  plexRelation: PlexUserRelationState;
  answerRelation: AnswerUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: answerRelation,
    getRequiredPermission: answerDeletePermission,
  });
}

export function canCreateBoard(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CREATE_PLEX_BOARD);
}

export function canEditBoard(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.EDIT_BOARD);
}

export function canChangeBoardOrder(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CHANGE_BOARD_ORDER);
}

export function canDeleteBoard(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.DELETE_BOARD);
}

export function canChangeQuestionBoard(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CHANGE_QUESTION_BOARD);
}

export function canChangeQuestionOrder(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CHANGE_QUESTION_ORDER);
}

export function canViewPlexAnalytics(plexRelation: PlexUserRelationState): boolean {
  return permissionSet(plexRelation).has(Permission.READ_PLEX_ANALYTICS);
}

export function canViewPostAnalytics(plexRelation: PlexUserRelationState): boolean {
  return permissionSet(plexRelation).has(Permission.READ_POST_ANALYTICS);
}

export function canViewQuestionAnalytics(plexRelation: PlexUserRelationState): boolean {
  return permissionSet(plexRelation).has(Permission.READ_QUESTION_ANALYTICS);
}

export function canCreateFactReviewProcess(plexRelation: PlexUserRelationState): boolean {
  return permissionSet(plexRelation).has(Permission.CREATE_FACT_REVIEW_PROCESS);
}

export function canCreateTask(role: PlexUserRelationState): boolean {
  return permissionSet(role).has(Permission.CREATE_PLEX_TASK);
}

export function canEditTask({
  plexRelation,
  taskRelation,
}: {
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: taskRelation,
    getRequiredPermission: taskEditPermission,
  });
}

export function canDeleteTask({
  plexRelation,
  taskRelation,
}: {
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
}): boolean {
  return hasContentPermission({
    plexRelation,
    contentRelation: taskRelation,
    getRequiredPermission: taskDeletePermission,
  });
}
